import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import DropdownMultiselect from '../Generic/DropdownMultiselect';
import { getSupplierSelectList } from '../../clients/supplier_client';
import globalSupplierSelectCache from '../../config_and_helpers/supplier_cache';
import {
  checkManufacturerName,
  createManufacturer,
  updateManufacturer,
} from '../../clients/manufacturer_client';
import * as yup from 'yup';
import { Formik } from 'formik';
import { Form, Spinner } from 'react-bootstrap';

import './ManufacturerComponentStyle.css';
import '../Glass/ProcureModalStyle.css';
import '../Glass/GlassStyle.css';
import { PencilFill } from 'react-bootstrap-icons';
import {
  showToast,
  updateObjectInObjectArray,
} from '../../config_and_helpers/helpers';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';
import CustomManufacturerContextHook from '../../pages/Manufacturer/CustomManufacturerContextHook';

/**
 * formSchema Yup Validation Schema
 *
 * A Yup validation schema for a form used to create or edit a manufacturer.
 * The form includes the fields 'name' and 'websiteUrl', each having its own validation.
 *
 * 'name' field: A string that is required. An error message "Manufacturer name is required" is shown if it is missing.
 * 'websiteUrl' field: A URL string that is required. An error message "Website is required" is shown if it is missing, and "Enter a valid URL" if URL is not valid.
 *
 * @type {Yup.object}
 */
const formSchema = yup.object().shape({
  name: yup.string().required('Manufacturer name is required'),
  websiteUrl: yup
    .string()
    .required('Website is required')
    .url('Enter a valid URL'),
});

/**
 * Modal component for creating/editing a manufacturer.
 *
 * @param {function} onSuccessfulSubmit - Function to be called when form is successfully submitted
 * @param {object} manufacturerObj - The manufacturer object to be edited (optional)
 * @param {function} clearSearchValue - A function to clear the parent component search value
 * @returns {React.Component} ManufacturerModal
 */
const ManufacturerModal = ({
  onSuccessfulSubmit,
  manufacturerObj,
  clearSearchValue,
  supplierList: propSupplierList = null,
  onEditButtonHover,
  isEditButtonLoading,
}) => {
  // The user's auth token from context
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  // Fetch the ManufacturerContext or provide fallback values
  const { manufacturers, setManufacturers } = CustomManufacturerContextHook();

  // State for selected related suppliers; initialized from the manufacturerObj prop, if present
  const [relatedSuppliers, setRelatedSuppliers] = useState(
    manufacturerObj && manufacturerObj.suppliers
      ? manufacturerObj.suppliers.map(item => ({
          value: item.id,
          label: item.name,
        }))
      : []
  );
  // State for the list of all suppliers for the multi-select dropdown
  const [supplierList, setSupplierList] = useState([]);
  // State for the entered manufacturer name; initialized from the manufacturerObj prop, if present
  const [name, setName] = useState(
    manufacturerObj ? manufacturerObj?.name : ''
  );
  // State for checking whether the entered manufacturer name is unique
  const [isManufacturerNameUnique, setIsManufacturerNameUnique] =
    useState(true);
  // State for checking whether the uniqueness check for the manufacturer name is in progress
  const [isCheckingManufacturerName, setIsCheckingManufacturerName] =
    useState(false);
  // State for checking whether form submission is in progress
  const [isSubmitting, setIsSubmitting] = useState(false);
  // State for controlling the visibility of the modal
  const [showModal, setShowModal] = useState(false);
  // State for loading suppliers
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);

  // Ref for debounce timeout
  const debounceTimeoutRef = useRef(null);

  // Function to fetch supplier select list on hover
  const fetchSupplierSelectList = useCallback(() => {
    // Use prop supplier list if provided, otherwise use global cache or fetch
    if (propSupplierList && propSupplierList.length > 0) {
      setSupplierList(propSupplierList);
      return;
    }

    // Check if cache exists and hasn't been invalidated
    const cacheIsValid = globalSupplierSelectCache.data && 
      (!globalSupplierSelectCache.invalidatedAt || 
       !globalSupplierSelectCache.timestamp ||
       globalSupplierSelectCache.timestamp > globalSupplierSelectCache.invalidatedAt);

    // Use global cache if available and valid
    if (cacheIsValid) {
      setSupplierList(globalSupplierSelectCache.data);
      return;
    }

    // If already fetching, wait for the promise
    if (
      globalSupplierSelectCache.isFetching &&
      globalSupplierSelectCache.promise
    ) {
      setIsLoadingSuppliers(true);
      globalSupplierSelectCache.promise.then(() => {
        if (globalSupplierSelectCache.data) {
          setSupplierList(globalSupplierSelectCache.data);
        }
        setIsLoadingSuppliers(false);
      });
      return;
    }

    // Fetch and cache the data
    setIsLoadingSuppliers(true);
    globalSupplierSelectCache.isFetching = true;
    globalSupplierSelectCache.promise = getSupplierSelectList(token, data => {
      globalSupplierSelectCache.data = data;
      globalSupplierSelectCache.timestamp = Date.now();
      globalSupplierSelectCache.isFetching = false;
      setSupplierList(data);
      setIsLoadingSuppliers(false);
    });

    globalSupplierSelectCache.promise.then(() => {
      globalSupplierSelectCache.isFetching = false;
    });
  }, [token, propSupplierList]);

  // Fetch supplier select list when modal is opened (fallback)
  useEffect(() => {
    if (showModal) {
      fetchSupplierSelectList();
    }
  }, [showModal, fetchSupplierSelectList]);

  // Validator function to check manufacturer name uniqueness
  const manufacturerNameUniqueValidator = {
    id: 'unique',
    text: 'Name already taken.',
    validate: () =>
      isCheckingManufacturerName ? true : isManufacturerNameUnique,
  };

  // Async function to validate manufacturer name
  const validateManufacturerName = useCallback(
    async value => {
      const response = await checkManufacturerName(token, value);
      setIsCheckingManufacturerName(false);
      setIsManufacturerNameUnique(response);
    },
    [token]
  );

  // Debounced function to validate manufacturer name, prevents multiple
  // requests on each key stroke, waits for the user to stop typing
  const debouncedCheckManufacturerName = useCallback(
    value => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        validateManufacturerName(value);
      }, 500);
    },
    [validateManufacturerName]
  );

  // Effect hook to validate manufacturer name whenever it changes
  useEffect(() => {
    if (name && name !== manufacturerObj?.name) {
      debouncedCheckManufacturerName(name);
    } else {
      setIsCheckingManufacturerName(false);
    }
  }, [name, debouncedCheckManufacturerName, manufacturerObj?.name]);

  // Cleanup effect for debounce timeout
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Effect to update relatedSuppliers when manufacturerObj changes (for editing)
  useEffect(() => {
    if (manufacturerObj && manufacturerObj.suppliers) {
      setRelatedSuppliers(
        manufacturerObj.suppliers.map(item => ({
          value: item.id,
          label: item.name,
        }))
      );
    } else if (manufacturerObj && !manufacturerObj.suppliers) {
      setRelatedSuppliers([]);
    }
  }, [manufacturerObj]); // Trigger when manufacturerObj changes

  // Functions to handle modal show and hide
  const handleClose = () => {
    setShowModal(false);
    setName('');
  };
  const handleShow = () => setShowModal(true);

  // Function to reset modal to initial state
  const resetModal = () => {
    if (!manufacturerObj) {
      setRelatedSuppliers([]);
    }
  };

  // Function to handle form submission
  function handleSubmit(values) {
    setIsSubmitting(true);

    // Prepare manufacturer data from form values
    const manufacturerData = {
      name: values.name,
      website: values.websiteUrl,
      suppliers: relatedSuppliers.map(supplier => supplier.value).join(','),
    };

    // Determine if creating or updating a manufacturer
    const manufacturerPromise = manufacturerObj
      ? updateManufacturer(
          token,
          manufacturerObj.id,
          manufacturerData,
          onSuccessfulSubmit
        )
      : createManufacturer(token, manufacturerData);

    // Executes function after form is submitted
    manufacturerPromise.then(response => {
      if (response && response.success) {
        setTimeout(() => {
          if (manufacturerObj) {
            setManufacturers(
              updateObjectInObjectArray(
                manufacturers,
                manufacturerObj.id,
                response.updatedManufacturer
              )
            );
          } else {
            onSuccessfulSubmit();
            if (clearSearchValue) clearSearchValue(); // If clearSearchValue is truthy, clear the search value in the parent component
          }
          response.toast(); // Displays a success toast notification
          handleClose(); // Closes the modal after submitting the form
          setIsSubmitting(false);
          if (!manufacturerObj) {
            resetModal(); // Resets the form to initial state
          }
        }, 1000);
      } else {
        showToast(
          // Function for showing error toast notification
          'An unexpected error occurred. Please try again in a little while.',
          'error',
          'top-right',
          3000
        );
        setIsSubmitting(false); // Stop showing submission spinner
      }
    });
  }

  if (isViewer) {
    return null;
  }

  return (
    <>
      {/* Button to trigger the modal; displays "Edit Manufacturer" for editing an existing manufacturer,
       "Create Manufacturer" for creating a new one */}
      <Button
        variant={manufacturerObj ? 'outline-success' : 'success'}
        onClick={handleShow}
        onMouseEnter={() => {
          fetchSupplierSelectList();
          if (onEditButtonHover) {
            onEditButtonHover();
          }
        }}
      >
        {manufacturerObj ? <PencilFill /> : 'Create Manufacturer'}
      </Button>
      {/* Modal for the create/edit form */}
      <Modal
        show={showModal}
        onHide={handleClose}
        backdrop="static"
        dialogClassName="procure-modal-glass procure-modal-glass-form"
      >
        <Modal.Header closeButton className="procure-modal__header">
          {/* Modal title differentiates between editing and creating */}
          <Modal.Title>
            {manufacturerObj
              ? `Edit Manufacturer ${
                  manufacturerObj.name || `#${manufacturerObj.id}`
                }`
              : 'Create Manufacturer'}
          </Modal.Title>
        </Modal.Header>

        {/* Show loading spinner when editing and data is being fetched */}
        {manufacturerObj && isEditButtonLoading ? (
          <Modal.Body
            className="procure-modal__body d-flex justify-content-center align-items-center"
            style={{ minHeight: '200px' }}
          >
            <div className="text-center">
              <Spinner animation="border" size="lg" className="mb-3" />
              <div className="text-muted">Loading manufacturer details...</div>
            </div>
          </Modal.Body>
        ) : (
          /* Formik component for form handling and validation */
          <Formik
            /* If editing, the form fields are initially marked as touched to enable validation feedback */
            initialTouched={
              manufacturerObj
                ? {
                    name: true,
                    websiteUrl: true,
                  }
                : {}
            }
            /* Initial values are set according to whether we're creating or editing a manufacturer */
            initialValues={{
              name: manufacturerObj ? manufacturerObj?.name : '',
              websiteUrl: manufacturerObj ? manufacturerObj?.website : '',
            }}
            /* Validate the form on mount if we're editing a manufacturer */
            validateOnMount={!!manufacturerObj}
            /* Reinitialize Formik when initialValues change */
            enableReinitialize={true}
            /* The validation schema to be used by Formik, defined by Yup */
            validationSchema={formSchema}
            /* Action to be performed when form is submitted */
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({
              handleChange,
              handleSubmit,
              values,
              handleBlur,
              touched,
              errors,
              isValid,
              dirty,
            }) => {
              return (
                // Form from react-bootstrap, using Formik's onSubmit function
                <Form noValidate onSubmit={handleSubmit}>
                  {/* Modal Body starts */}
                  <Modal.Body className="procure-modal__body d-flex flex-column">
                    {/* Form input group for Manufacturer Name */}
                    <Form.Group
                      controlId="formManufacturerName"
                      className="field-margin"
                    >
                      <Form.Label>
                        Manufacturer Name {<RequiredAsteriskComponent />}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={values.name}
                        // When the input changes, update the state and start process to validate the uniqueness of the manufacturer name
                        onChange={event => {
                          const { value } = event.target;
                          handleChange(event);
                          setIsCheckingManufacturerName(true);
                          setName(value);
                        }}
                        onBlur={handleBlur}
                        // Field is marked as invalid if it has a validation error message or if the manufacturer name is not unique
                        isInvalid={
                          (touched.name && !!errors.name) ||
                          (!manufacturerNameUniqueValidator.validate() && name)
                        }
                        // Field is marked as valid if touched and no error, and manufacturer name is unique
                        isValid={
                          !errors.name &&
                          manufacturerNameUniqueValidator.validate() &&
                          name &&
                          !isCheckingManufacturerName &&
                          isManufacturerNameUnique
                        }
                      />
                      {/* Validation feedback messages */}
                      {manufacturerNameUniqueValidator.validate() &&
                        name &&
                        !isCheckingManufacturerName && (
                          <Form.Control.Feedback type="valid">
                            Looks good!
                          </Form.Control.Feedback>
                        )}
                      <Form.Control.Feedback type="invalid">
                        {/* Displays error message if input value doesn't meet validation criteria */}
                        {errors.name}
                        {/* If manufacturer name isn't unique, show error message */}
                        {!manufacturerNameUniqueValidator.validate() &&
                          !isCheckingManufacturerName &&
                          name &&
                          manufacturerNameUniqueValidator.text}
                      </Form.Control.Feedback>

                      {/* Loading message while checking for name uniqueness */}
                      {isCheckingManufacturerName && (
                        <Form.Text className="text-muted">
                          Checking...
                        </Form.Text>
                      )}
                    </Form.Group>

                    {/* Form input group for Website Link */}
                    <Form.Group controlId="formWebsiteUrl" className="mb-4">
                      <Form.Label>
                        Website Link {<RequiredAsteriskComponent />}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="websiteUrl"
                        value={values.websiteUrl}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.websiteUrl && !!errors.websiteUrl}
                        isValid={
                          touched.websiteUrl &&
                          !errors.websiteUrl &&
                          values.websiteUrl
                        }
                      />
                      <Form.Control.Feedback type="valid">
                        Looks good!
                      </Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        {errors.websiteUrl}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* Component to select suppliers */}
                    {isLoadingSuppliers && supplierList.length === 0 ? (
                      <div className="d-flex align-items-center">
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        <span className="text-muted">Loading suppliers...</span>
                      </div>
                    ) : (
                      <DropdownMultiselect
                        key={`supplier-dropdown-${supplierList.length}`}
                        optionsList={supplierList}
                        selectedValues={relatedSuppliers}
                        setSelectedValues={setRelatedSuppliers}
                        placeholder="Suppliers"
                      />
                    )}
                  </Modal.Body>

                  {/* Modal Footer with the action buttons */}
                  <Modal.Footer className="procure-modal__footer">
                    {isSubmitting ? (
                      <Button variant="primary" className="glass-primary-btn" disabled>
                        <Spinner
                          size="sm"
                          as="span"
                          animation="border"
                          role="status"
                          aria-hidden="true"
                        />
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        className="glass-primary-btn"
                        disabled={
                          !isValid ||
                          (!manufacturerObj && !dirty) ||
                          !manufacturerNameUniqueValidator.validate() ||
                          isCheckingManufacturerName
                        }
                        onClick={handleSubmit}
                      >
                        {manufacturerObj ? 'Save' : 'Create'}
                      </Button>
                    )}

                    <Button
                      variant="secondary"
                      className="procure-modal__close-btn"
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        )}
      </Modal>
    </>
  );
};
export default ManufacturerModal;
